import prepareArticleStructuredPageData from "@DSG/Common/Utilities/prepareArticleStructuredPageData";
import Facebook from "@DSG/MobiusOverrides/Icons/Facebook";
import Linkedin from "@DSG/MobiusOverrides/Icons/Linkedin";
import colorPallete, { bodyFonts } from "@DSG/MobiusOverrides/styles/variables";
import setPageMetadata from "@DSG/Overrides/Common/Utilities/setPageMetadata";
import { parserOptions } from "@insite/client-framework/Common/BasicSelectors";
import { emptyGuid } from "@insite/client-framework/Common/StringHelpers";
import StyledWrapper from "@insite/client-framework/Common/StyledWrapper";
import getJsonLdScriptId from "@insite/client-framework/Common/Utilities/getJsonLdScriptId";
import ApplicationState from "@insite/client-framework/Store/ApplicationState";
import { setMetadata } from "@insite/client-framework/Store/CommonHandlers/SetMetadata";
import { getSettingsCollection } from "@insite/client-framework/Store/Context/ContextSelectors";
import { getCurrentPage, getLocation } from "@insite/client-framework/Store/Data/Pages/PageSelectors";
import translate from "@insite/client-framework/Translate";
import WidgetModule from "@insite/client-framework/Types/WidgetModule";
import WidgetProps from "@insite/client-framework/Types/WidgetProps";
import LocalizedDateTime from "@insite/content-library/Components/LocalizedDateTime";
import Img from "@insite/mobius/Img/Img";
import Link from "@insite/mobius/Link/Link";
import Typography, { TypographyPresentationProps, TypographyProps } from "@insite/mobius/Typography";
import { BaseTheme } from "@insite/mobius/globals/baseTheme";
import InjectableCss from "@insite/mobius/utilities/InjectableCss";
import breakpointMediaQueries from "@insite/mobius/utilities/breakpointMediaQueries";
import parse from "html-react-parser";
import * as React from "react";
import { connect } from "react-redux";
import { css } from "styled-components";

const enum fields {
    facebookIconImage = "facebookIconImage",
    linkedInIconImage = "linkedInIconImage",
}


interface OwnProps extends WidgetProps {
    fields: {
        [fields.facebookIconImage]: string;
        [fields.linkedInIconImage]: string;
    };
}

const mapStateToProps = (state: ApplicationState) => {
    const defaultLanguages = state.context.website.languages!.languages!.filter(o => o.isDefault);
    return{
        page: getCurrentPage(state),
        defaultLanguageId: defaultLanguages.length > 0 ? defaultLanguages[0].id : emptyGuid,
        currentLanguageId:state.context.session.language!.id,
         websiteSettings: getSettingsCollection(state).websiteSettings,
         location: getLocation(state),
         website:state.context.website,
    }
   
};

type Props = WidgetProps & ReturnType<typeof mapStateToProps> & OwnProps;

export interface NewsPageViewStyles {
    authorText?: TypographyProps;
    publishDateText?: TypographyProps;
    newsWrapper?: InjectableCss;
    contentWrapper?: InjectableCss;
    dateAndAuthorWrapper?: InjectableCss;
    shareArticleWrapper?: InjectableCss;
    shareArticleIconWrapper?: InjectableCss;
    shareArticleText?: TypographyPresentationProps;
}

export const newsPageViewStyles: NewsPageViewStyles = {
    newsWrapper: {
        css: css`
            margin-top: 24px !important;
            border-top: 1px solid ${colorPallete.grey300};
            padding-top: 24px;
            ${({ theme }: { theme: BaseTheme }) =>
            breakpointMediaQueries(
                theme,
                [
                    null,
                    null,
                    css`
                        margin-top: 24px !important;
                        padding-top: 24px;
                    `,
                ],
                "min",
            )}
        `
    },
    contentWrapper: {
        css: css`
            margin-top: 8px;
            p[class*="TypographyStyle"] {
                &:last-child {
                    margin-bottom: 0;
                }
            }
        `,
    },
    authorText: {
        css: css`
            font-weight: bold;
            margin-right: 8px;
        `,
    },
    publishDateText: {
        css: css`
        `,
    },
    shareArticleWrapper: {
        css: css`
            display: flex;
            align-items: center;
            gap: 16px;
            padding-top: 16px;
            margin-top: 24px;
            border-top: 1px solid ${colorPallete.grey300};
            justify-content: flex-end;
            ${({ theme }: { theme: BaseTheme }) =>
            breakpointMediaQueries(
                theme,
                [
                    null,
                    null,
                    css`
                        padding-top: 20px;
                        margin-top: 20px;
                        justify-content: center;
                    `,
                ],
                "max",
            )}
        `
    },
    shareArticleIconWrapper: {
        css: css`
            display: flex;
            gap: 8px;
            align-items: center;
            a {
                padding: 4px;
                > span {
                    display: flex;
                }
                &,
                &:hover,
                &:focus {
                    > span {
                        &[class*="StyledTypography"] {
                            color: ${colorPallete.grey800} !important;
                        }
                    }
                }
            }
            img, svg {
                width: 34px;
                height: 34px;    
            }
        `
    },
    shareArticleText: {
        css: css`
            font: ${bodyFonts.body2SemiBoldLg.fontWeight} ${bodyFonts.body2SemiBoldLg.fontSize}/${bodyFonts.body2SemiBoldLg.lineHeight} ${bodyFonts.body2SemiBoldLg.fontFamily};
        `
    },

};

const styles = newsPageViewStyles;

const NewsPageView = ({ 
    page,
    currentLanguageId,
    defaultLanguageId,
    websiteSettings,
    location,
    website, 
    fields, }: Props) => {
    if (!page) {
        return null;
    }
  const  setMetadata = ()=> {
        const languageKey = currentLanguageId ?? defaultLanguageId;
        
        
        const {
            metaDescription,
            metaKeywords,
            openGraphImage,
            openGraphTitle,
            openGraphUrl,
            title,
            primaryImagePath,
            alternateLanguageUrls,
        } = page.translatableFields;

        
        setPageMetadata(
            {
                metaDescription: metaDescription[languageKey],
                metaKeywords:metaKeywords[languageKey],
                openGraphImage: openGraphImage[languageKey] || primaryImagePath,
                openGraphTitle:openGraphTitle[languageKey],
                openGraphUrl:openGraphUrl[languageKey],
                title:title[languageKey],
                currentPath: location.pathname,
                alternateLanguageUrls: alternateLanguageUrls ?? undefined,
                websiteName:website.name,
                structuredPageData: prepareArticleStructuredPageData(languageKey,page,website),
                jsonLdScriptId: getJsonLdScriptId(page.type),
            },
            websiteSettings,
        );
    }
    React.useEffect(() => {
        setMetadata();
    }, []);
   
    const pageTitle = page.translatableFields.title[currentLanguageId ?? defaultLanguageId];
    if (typeof window !== "undefined") {
        const domainUrl = window.location.origin;
    }

    return (
        <StyledWrapper {...styles.newsWrapper} key={page.id} className="cms-content">
            <StyledWrapper {...styles.dateAndAuthorWrapper}>
                {page.fields["author"] && (
                    <Typography {...styles.authorText} data-test-selector="newslist_author">
                        {page.fields["author"]}
                    </Typography>
                )}
                {page.fields["publishDate"] && (
                    <Typography {...styles.publishDateText} data-test-selector="newslist_publishdate">
                        <LocalizedDateTime
                            dateTime={new Date(page.fields["publishDate"])}
                            options={{ year: "numeric", month: "2-digit", day: "2-digit" }}
                        />
                    </Typography>
                )}
            </StyledWrapper>
            {(page.fields["newsSummary"] || page.fields["newsContent"]) && (
                <StyledWrapper {...styles.contentWrapper} data-test-selector="newslist_content">
                    {parse(page.fields["newsContent"], parserOptions)}
                </StyledWrapper>
            )}

            {((page.fields["facebookNavigationLink"]) || (page.fields["linkedInNavigationLink"])) && (
                <StyledWrapper {...styles.shareArticleWrapper}>
                    <Typography {...styles.shareArticleText}>{translate("Share article:")}</Typography>
                    <StyledWrapper {...styles.shareArticleIconWrapper}>
                        {page.fields["facebookNavigationLink"] && (
                            <Link href={page.fields["facebookNavigationLink"]}
                                target="_blank"
                                aria-label={translate('Share current ') + pageTitle + translate('news page on Facebook. This will open in a new window.')}>
                                {fields.facebookIconImage && (
                                    <Img src={fields.facebookIconImage} altText="" />
                                )}
                                {!fields.facebookIconImage && (
                                    <Facebook />
                                )}
                            </Link>
                        )}
                        {page.fields["linkedInNavigationLink"] && (
                            <Link href={page.fields["linkedInNavigationLink"]}
                                target="_blank"
                                aria-label={translate('Share current ') + pageTitle + translate('news page on Linkedin. This will open in a new window.')}>
                                {fields.linkedInIconImage && (
                                    <Img src={fields.linkedInIconImage} altText="" />
                                )}
                                {!fields.linkedInIconImage && (
                                    <Linkedin />
                                )}
                            </Link>
                        )}
                    </StyledWrapper>
                </StyledWrapper>
            )}
        </StyledWrapper>
    );
};

const widgetModule: WidgetModule = {
    component: connect(mapStateToProps)(NewsPageView),
    definition: {
        group: "News Page",
        allowedContexts: ["NewsPage"],
        fieldDefinitions: [
            {
                name: fields.facebookIconImage,
                defaultValue: "",
                editorTemplate: "ImagePickerField",
                fieldType: "Translatable",
                sortOrder: 0,
            },
            {
                name: fields.linkedInIconImage,
                defaultValue: "",
                editorTemplate: "ImagePickerField",
                fieldType: "Translatable",
                sortOrder: 1,
            },
        ],
    },
};

export default widgetModule;